import { BalanceData, DamageType, LevelData, TileKind, bossHpMultOf } from "../data/balance";
import { ChestResult, battleChestBand, battleChestBump, rollChest } from "./chest";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";
import { Cell } from "../world/cell";
import { Grid } from "../world/grid";
import { RoadMap } from "../world/roadMap";
import { Command, CommandKind, CommandResult, TargetPriority } from "./command";
import { TroopSystem, troopSpawnIntervalTicks, troopStatMult, troopStatMultWithBonus } from "./troops";
import { applyDamage, fireTowers, recomputeAuras } from "./systems/combat";
import { moveEnemies, spawn, spawnDue, tickBurns, tickHealers, tickShields } from "./systems/enemyMotion";
import { landAllShells, tickShells } from "./systems/mortar";
import { tickHexes } from "./systems/hex";
import { tickEnemyAbilities } from "./systems/enemyAbilities";
import { isTargetable } from "./systems/targeting";
import { beamMultiplier, beamRamp } from "./systems/beam";
import { Squad, disbandSquad, promoteSquad, raiseSquad, rerouteSquads, soldierRespawnTicks, tickSquads } from "./barracks";
import { checkOfferThreshold, closeOffer, moveTower, nextOpeningOffer, pickCard, placeSegment, placeTower, reroll, sellTower, setPriority, skipOffer, skipReward, unpickCard, upgradeTower } from "./systems/draftFlow";
import { computeHash } from "./systems/simHash";
import { GearBonus, NO_GEAR_BONUS } from "../meta/gear";
import { SimConstants } from "./constants";
import { CardKind, Enemy, GroundZone, Shell, Tower } from "./entities";
import { Ascension, BASE_TIERS, canRaise, chooseBranch, debugSetBranch, skillRangeBonus, learnSkill, resetBranchState, tierLine, typeBranch } from "./branches/ascension";
import { ShotContext } from "./branches/hits";
import { Phoenix, Spider } from "./branches/minions";
import { fireInterval } from "./branches/shots";
import { clearZones } from "./branches/statuses";
import { tickBranches } from "./branches/tick";
import { EventBuffer, SimEventKind } from "./events";
import { nextShownOfferAt, offersUntilTowers, offersUntilUpgrade, pickDelay, pointsToNextOffer } from "./cardPacing";
import { Offer } from "./offer";
import { OfferGenerator } from "./offerGenerator";
import { SpawnEntry } from "./waveSchedule";
import { runStages, stageSchedule } from "./runModes";
import { LevelModifiers, NO_MODIFIERS, ResolvedModifiers, resolveModifiers } from "./modifiers";
import { MythicHooks, mythicValue, resolveMythicHooks } from "./mythics";
import { RareRun, ironRareWave, rareEntry, rareStageSchedule, tickRareOffers } from "./rares";
import { armRallyHorn, rallyHornAtStage } from "./rallyHorn";
import { petSpawnInterval } from "./pet";
import { PetSystem, petSystemFor } from "./petSystem";
import { WildPetRun, engagePet, tickWildPet, wildPetAtStage, wildPetEncounter } from "./wildPet";
import { WyrmRun, wyrmEntry, wyrmRelease, wyrmRetry, wyrmStageSchedule } from "./wyrm";

/** Boss mechanic numbers (DESIGN §5). Kept here rather than in balance so every boss reads one table. */
export const BossRules = {
  /** Warlord: allies within this many cells move this much faster. */
  warlordRadius: Fix64.fromInt(3), warlordSpeedMult: Fix64.parse("1.25"),
  /** Necromancer: every Nth hit spawns a Grunt beside the boss. */
  necromancerHitsPerSpawn: 5,
  /** Ironclad: armour rises by this much as HP falls from full to zero. */
  ironcladArmourGain: Fix64.parse("0.5"),
} as const;

/** debugSpawn's fractional HP multiplier resolution (thousandths): a dev-console number becomes an exact Fix64. */
const DEBUG_HP_MULT_STEPS = 1000;
const MS_PER_SECOND = 1000;

export enum LevelPhase { OpeningDraft = 0, Ready = 1, WaveRunning = 2, BetweenWaves = 3, Won = 4, Lost = 5 }


/**
 * One level run: the card draft, the road, towers, enemies and waves. Deterministic given
 * (balance, level, seed, owned towers, tower level, commands). Mutate only through execute() and step().
 */
export class LevelSim {
  readonly level: LevelData;
  readonly grid: Grid;
  readonly road: RoadMap;
  readonly events = new EventBuffer();
  readonly stages: number;
  readonly enemySlots: Enemy[] = [];
  readonly towerSlots: Tower[] = [];
  /** The base's troops and the rally flag (DESIGN §7); Barracks soldiers live in `troops.soldiers` too. */
  readonly troops: TroopSystem;
  /** Barracks squads, one per standing Barracks, in placement order (sim/barracks.ts, TOWERS_V2). */
  readonly squads: Squad[] = [];
  /** Mortar shells in flight, in launch order (sim/systems/mortar.ts). */
  readonly shells: Shell[] = [];
  /** The shells that landed this tick (cleared at the start of the next wave tick): a view reads a ShellLanded's point here. */
  readonly landedShells: Shell[] = [];
  nextShellId = 1;
  /** Whether any enemy wore a Hex curse after the last hex pass (lets a level with no totem skip the pass). */
  anyHexed = false;
  /** Tier 4 (M21, sim/branches, docs/TOWERS_V2.md "Tier 4"): the run's branch choices, learned skills and the special card's pace. */
  readonly ascension: Ascension;
  /** Tier-4 ground zones in laying order (Thorn Trap, Firestorm, Plague clouds, Earthquake, Napalm). */
  readonly zones: GroundZone[] = [];
  nextZoneId = 1;
  /** One phoenix per tier-4 Phoenix Roost, in placement order. */
  readonly phoenixes: Phoenix[] = [];
  /** The Venom Nests' spiders, by nest in placement order. */
  readonly spiders: Spider[] = [];
  nextSpiderId = 1;
  /**
   * @internal >0 while area damage is being dealt (splash, chain, cone, cluster, zone, damage aura, blast): applyDamage
   * cuts it against heavy enemies (M30 #14). Raised and lowered around each area loop within one tick; never hashed.
   */
  areaDepth = 0;
  /** @internal the shot being fired (sim/branches/hits.ts). */
  readonly shot: ShotContext = { towerId: 0, stunTicks: 0, first: false, periodic: false };
  readonly rng: Pcg32;
  readonly owned: boolean[];
  readonly generator: OfferGenerator;
  readonly offer = new Offer();
  /** Persistent level per tower archetype (base archetype included), resolved from the constructor argument. */
  readonly towerLevels: readonly number[];
  schedule: SpawnEntry[] = [];
  offerOpen = false;
  offerCounter = 0;
  openingPathLeft: number;
  openingTowerLeft: number;
  spawnCursor = 0;
  /** Tick the current stage's clock counts from; moved forward by every tick paused for an offer (see step()). */
  stageStartTick = 0;
  /**
   * Index in `schedule` of the newest stage's first spawn (M30 #17): a call during a running wave waits until it has
   * spawned, so each call brings one more stage onto the road rather than all of them at once.
   */
  newestStageFirst = 0;
  nextEnemyId = 1;
  nextTowerId = 1;
  _aliveEnemies = 0;

  phase = LevelPhase.OpeningDraft;
  tick = 0;
  /** Ticks stepped with the battle running (no card offer open, not over): Race Week's time (clearMs). Not hashed. */
  activeTicks = 0;
  /** Points earned from kills this run; card offers open at rising thresholds (§4.4). */
  points = 0;
  /** Points needed for the next offer. */
  nextOfferAt: number;
  offersOpened = 0;
  /** In-run offers since the last one that was due tower cards / since the last Upgrade taken (card pacing, §4.4). */
  offersSinceTowers = 0;
  offersSinceUpgrade = 0;
  /** In-run offers since the last road piece was laid (the road pace, §4.4). */
  offersSinceRoad = 0;
  chest: ChestResult | null = null;
  /**
   * Rare spawns (M36, sim/rares.ts): the run's rare once one has appeared (`outcome` alive / killed / escaped), the stage
   * that is rare (0 = none), the chest a killed rare paid (banked by the app at once), the rally offers still to open
   * and the running ticks before the next may. `forceRareNextStage` is the dev hook's flag (never hashed).
   */
  rareRun: RareRun | null = null;
  /** Every rare of the run in order (M37: Endless may bring several, `endlessCooldownStages` apart); `rareRun` is the last. */
  readonly rareRuns: RareRun[] = [];
  rareStage = 0;
  rareChest: ChestResult | null = null;
  rareOffersLeft = 0;
  rareOfferCountdown = 0;
  forceRareNextStage = false;
  /**
   * The Starfall Wyrm (M40, sim/wyrm.ts): the run's Wyrm once it has appeared, the stage that is its (0 = none), the
   * choice open after it flew off (the action pauses on it), the dev hook's flag (never hashed).
   */
  wyrmRun: WyrmRun | null = null;
  wyrmStage = 0;
  wyrmChoiceOpen = false;
  forceWyrmNextStage = false;
  /** Pets v2 (sim/petSystem.ts, sim/wildPet.ts): the pet on the flag and its abilities' clocks (null: no pet), the run's wild pet (null: none). */
  readonly petSystem: PetSystem | null;
  readonly wildPet: WildPetRun | null;
  baseHp: number;
  /** Current stage, 1-based; 0 before the first wave. */
  stage = 0;
  stagesCleared = 0;
  kills = 0;
  leaks = 0;
  /** Dev only: no scheduled spawns, no stage end, no defeat — for showcase captures. */
  debugSandbox = false;
  /** Dev only: leaks cost the base nothing, so a capture can never end in defeat. */
  debugNoDefeat = false;
  rerollCharges = 0;
  /** Ticks until the next stage starts automatically (BetweenWaves without an open offer). */
  countdown = 0;

  /** Per tower archetype (base at its own index): what equipped gear contributes (§9.4). Fixed for the run, hashed. */
  readonly gear: readonly GearBonus[];
  /** Mythic effect indexes resolved by id once, so the hot paths compare integers. */
  readonly mythicArcherChain: number;
  readonly mythicCannonDoubleSplash: number;
  readonly mythicFrostFreeze: number;
  /** The Fire, Lightning, Ballista, Poison and Beacon Mythic hooks (sim/mythics.ts). */
  readonly mythics: MythicHooks;
  /** The level's elite rules merged with the run's modifiers (sim/modifiers.ts); fixed for the run. */
  readonly mods: ResolvedModifiers;

  constructor(readonly balance: BalanceData, readonly levelNumber: number, readonly seed: bigint, ownedTowers: readonly boolean[], towerLevel: number | readonly number[] = 1, readonly firstClear = false, gear: readonly GearBonus[] = [], readonly modifiers: LevelModifiers = NO_MODIFIERS) {
    this.gear = balance.towers.map((_, i) => gear[i] ?? NO_GEAR_BONUS);
    const mythicIndex = (id: string): number => balance.gear.mythicEffects.find((m) => m.id === id)?.index ?? -99;
    this.mythicArcherChain = mythicIndex("archerChain");
    this.mythicCannonDoubleSplash = mythicIndex("cannonDoubleSplash");
    this.mythicFrostFreeze = mythicIndex("frostFreeze");
    this.mythics = resolveMythicHooks(balance);
    this.level = balance.level(levelNumber);
    this.mods = resolveModifiers(this.level, modifiers);
    this.rng = new Pcg32(seed);
    this.ascension = new Ascension(balance.towers.length, balance.branches.skills.length);
    // Iron (docs/MODES.md): the draft deals only the run's Iron towers, so the run owns only those.
    // MP-21: an Odyssey's deck the same way (an event run with no towers listed keeps the player's own).
    const only = this.mods.iron?.towers ?? (this.mods.event && this.mods.event.towers.length > 0 ? this.mods.event.towers : null);
    this.owned = only ? ownedTowers.map((o, i) => o && only.includes(i)) : [...ownedTowers];
    this.generator = new OfferGenerator(balance, levelNumber);
    this.grid = Grid.fromLevel(this.level);
    this.road = new RoadMap(this.grid);
    this.road.continuationShapes = balance.cards.shapes.filter((s) => s.unlockLevel <= levelNumber);
    this.stages = runStages(balance, levelNumber, this.mods);
    this.nextOfferAt = balance.cards.offerThreshold(1);
    this.towerLevels = typeof towerLevel === "number" ? balance.towers.map(() => towerLevel) : balance.towers.map((_, i) => towerLevel[i] ?? 1);
    for (let i = 0; i < SimConstants.MaxEnemies; i++) this.enemySlots.push(new Enemy());
    for (let i = 0; i < SimConstants.MaxTowers; i++) this.towerSlots.push(new Tower());
    const baseArchetype = balance.towers.findIndex((t) => t.isBase);
    const baseLevel = baseArchetype >= 0 ? this.towerLevels[baseArchetype] : 1;
    const troopCapEffect = mythicIndex("baseTroopCap");
    const capBonus = baseArchetype >= 0 && troopCapEffect >= 0 && this.gear[baseArchetype].mythic.includes(troopCapEffect) ? 2 : 0;
    const troopMult = troopStatMultWithBonus(troopStatMult(balance.economy.troops, baseLevel), baseArchetype >= 0 ? this.gear[baseArchetype].troops : undefined);
    this.troops = new TroopSystem(balance.economy.troops, this.grid, this.road, this.events, troopMult,
      petSpawnInterval(troopSpawnIntervalTicks(balance.economy.troops, baseLevel, balance.tables.maxLevel), this.mods.pet), capBonus);
    this.troops.onSoldierDied = (s) => soldierRespawnTicks(this, s);
    // MP-21 Odyssey: a map starts with the life pool the maps before it left (0 = full).
    const lives = this.mods.event?.lives ?? 0;
    this.baseHp = lives > 0 ? Math.min(lives, this.maxBaseHp) : this.maxBaseHp;
    // MP-06 supplies: the Supply Wagon's reroll charges from the start, the Rally Horn's squad (sim/rallyHorn.ts)
    this.rerollCharges = this.mods.supplies?.rerolls ?? 0;
    armRallyHorn(this);
    this.petSystem = petSystemFor(this.mods.pet);
    this.wildPet = wildPetEncounter(this);
    this.openingPathLeft = balance.cards.openingPathOffers;
    this.openingTowerLeft = balance.cards.openingTowerOffers;
    this.placeBaseTower();
    nextOpeningOffer(this);
  }

  /** The base fights back (§7): a permanent tower on the base cell, never offered or sold. */
  /** @internal used by the system modules in ./systems */
  placeBaseTower(): void {
    const t = this.allocateTower();
    t.id = this.nextTowerId++;
    t.archetype = this.balance.baseTowerIndex;
    t.alive = true;
    t.cell = this.grid.base;
    t.tier = 1;
    t.cooldown = 0;
    t.disabledTicksLeft = 0;
    t.priority = TargetPriority.First;
    t.kills = 0;
    t.auraDamageMult = Fix64.One;
    t.auraPointsMult = Fix64.One;
    t.beamTarget = 0;
    t.beamTicks = 0;
    resetBranchState(t);
  }

  get baseTower(): Tower { return this.towerSlots[0]; }

  get maxBaseHp(): number {
    const baseIndex = this.balance.towers.findIndex((t) => t.isBase);
    const hp = baseIndex >= 0 ? this.gear[baseIndex].hp : Fix64.Zero;
    const full = Fix64.fromInt(this.balance.economy.baseHp).mul(Fix64.One.add(hp)).floorToInt();
    // MP-28: a challenge code may cap the Fortress's health (0 = no cap); gear and VIP never lift it past the cap
    const cap = this.mods.challenge?.livesCap ?? 0;
    return cap > 0 ? Math.min(full, cap) : full;
  }
  /** Ticks between shots after gear fire-rate bonuses (never below 1). */
  fireIntervalOf(t: Tower): number {
    // Tier 4's interval multiplier and Overheat, then gear fire rate and aura haste (sim/branches/shots.ts).
    return fireInterval(this, t, this.balance.towers[t.archetype].fireIntervalTicks, this.gear[t.archetype].fireRate);
  }
  hasMythic(t: Tower, effect: number): boolean { return effect >= 0 && this.gear[t.archetype].mythic.includes(effect); }
  get currentOffer(): Offer | null { return this.offerOpen ? this.offer : null; }
  get ownedTowers(): readonly boolean[] { return this.owned; }
  get aliveEnemies(): number { return this._aliveEnemies; }
  /** An Endless run (docs/MODES.md): waves without end; `stages` is only the safety cap. */
  get isEndless(): boolean { return this.mods.endless !== null; }
  /** An Iron challenge (docs/MODES.md): one long wave, three towers. */
  get isIron(): boolean { return this.mods.iron !== null; }
  /** A Daily Road run (MP-20, docs/MODES.md §4): the day's road, the same for every player. */
  get isDaily(): boolean { return this.mods.daily !== null; }
  /** An event-mode run (MP-21, docs/MODES.md §5): a Boss Week tier, Race Week's road or an Odyssey map. */
  get isEventRun(): boolean { return this.mods.event !== null; }
  /** Race Week's clock (MP-21): sim milliseconds of the ticks the battle ran, not the ones paused for a card offer. */
  get clearMs(): number { return this.activeTicks * MS_PER_SECOND / SimConstants.TicksPerSecond; }
  /** A challenge-code run (MP-28, docs/MODES.md §7): the code's run, the same for every player. */
  get isChallenge(): boolean { return this.mods.challenge !== null; }
  get isOver(): boolean { return this.phase === LevelPhase.Won || this.phase === LevelPhase.Lost; }
  /** The rare on the road now (M36): {enemyId, archetype, chapter, stage, startedTick}, or null (none yet, killed or got in). */
  get rare(): RareRun | null { return this.rareRun !== null && this.rareRun.outcome === "alive" ? this.rareRun : null; }
  /** The Starfall Wyrm on the road now (M40), or null (none yet, slain, flown off or let go). */
  get wyrm(): WyrmRun | null { return this.wyrmRun !== null && this.wyrmRun.outcome === "alive" ? this.wyrmRun : null; }
  get spawnedThisStage(): number { return this.spawnCursor; }
  get scheduledThisStage(): number { return this.schedule.length; }
  /**
   * Read-only: the spawns stage `stage` will run, for the HUD's wave preview (packages/web). Built exactly as
   * `startStage` builds it and touches no state, so asking never changes a replay. Empty outside 1..stages.
   */
  previewStage(stage: number): readonly SpawnEntry[] {
    if (stage < 1 || stage > this.stages) return [];
    if (this.isIron && (this.rareStage > 0 || this.wyrmStage > 0)) return ironRareWave(this, this.balance.economy.firstStageDelayTicks);   // M37/M40: Iron's rare and Wyrm blocks
    if (stage === this.wyrmStage) return [wyrmEntry(this, stage, stage === 1 ? this.balance.economy.firstStageDelayTicks : 0)];   // M40: known once it starts
    if (stage === this.rareStage) return [rareEntry(this, stage, stage === 1 ? this.balance.economy.firstStageDelayTicks : 0)];   // M36: known once it starts
    return stageSchedule(this.balance, this.level, this.levelNumber, stage, this.stages, stage === 1 ? this.balance.economy.firstStageDelayTicks : 0, this.mods, this.seed);
  }

  /** Card pacing for the HUD (DESIGN §4.4, ./cardPacing): pure reads, never part of the hash. */
  get offersUntilTowers(): number { return offersUntilTowers(this); }
  get offersUntilUpgrade(): number { return offersUntilUpgrade(this); }
  get pointsToNextOffer(): number { return pointsToNextOffer(this); }
  /** Points total at which the next offer that will show opens (thresholds with nothing due pass unseen). */
  get nextShownOfferAt(): number { return nextShownOfferAt(this); }
  /** Points placing a card of this kind from the open offer adds to the next offer's threshold. */
  pickDelay(kind: CardKind): number { return pickDelay(this, kind); }

  /** Whether the action is paused for a card offer (opening draft excluded) or the Wyrm's choice (M40). */
  get pausedForOffer(): boolean { return (this.offerOpen && this.phase !== LevelPhase.OpeningDraft) || this.wyrmChoiceOpen; }

  findTower(id: number): Tower | null {
    for (const t of this.towerSlots) if (t.alive && t.id === id) return t;
    return null;
  }

  findEnemy(id: number): Enemy | null {
    for (const e of this.enemySlots) if (e.alive && e.id === id) return e;
    return null;
  }

  /** The range a tower of this archetype would have at `tier`, before it exists (placement preview). */
  previewRange(archetype: number, tier = 1): Fix64 {
    const a = this.balance.towers[archetype];
    const g = this.gear[archetype].range;
    // Mythic "Wider Beacon" adds whole cells before the gear bonus scales the range.
    let base = a.range.add(tierLine(this, archetype, tier).rangeBonus).add(mythicValue(this, archetype, this.mythics.beaconRange));
    if (tier > BASE_TIERS) base = base.add(skillRangeBonus(this, archetype));   // Eagle Eye
    return g.isZero() ? base : base.mul(Fix64.One.add(g));
  }

  /** The tower's range now: its type, tier and gear, plus what an aura lends it (Guiding Light). */
  rangeOf(t: Tower): Fix64 {
    const r = this.previewRange(t.archetype, t.tier);
    return t.auraRangeBonus.isZero() ? r : r.add(t.auraRangeBonus);
  }

  /** Arcane Spire (TOWERS_V2): its beam's ramp now, 0 (just locked on, or no target) to 1 (full). Zero for any other tower. */
  beamRamp(t: Tower): Fix64 { return beamRamp(this, t); }
  /** Arcane Spire: the damage multiplier its next pulse carries (1 at no ramp, rampMax (+ Overcharge) at full). */
  beamMultiplier(t: Tower): Fix64 { return beamMultiplier(this, t); }

  damageOf(t: Tower): Fix64 { return this.damageAtTier(t, t.tier); }

  /**
   * The damage `t` would deal at `tier`, with everything else about it as it is now (level, gear, auras). Read-only:
   * the Upgrade card's before/after preview asks for `t.tier + 1`; combat only ever asks through `damageOf`.
   */
  damageAtTier(t: Tower, tier: number): Fix64 {
    const a = this.balance.towers[t.archetype];
    const g = this.gear[t.archetype].damage;
    // Tier 4 (sim/branches): the branch's damage line; a Glacier's shards give the damageless Frost a flat damage.
    const raw = tier > BASE_TIERS && a.damage.isZero() ? typeBranch(this, t.archetype)?.tier4.num("flatDamage") ?? Fix64.Zero : a.damage;
    const base = raw.mul(tierLine(this, t.archetype, tier).damageMult).mul(this.balance.tables.levelMultAt(this.towerLevels[t.archetype])).mul(t.auraDamageMult.isZero() ? Fix64.One : t.auraDamageMult);
    return g.isZero() ? base : base.mul(Fix64.One.add(g));
  }

  /** Whether an Upgrade card can raise `t`: below tier 3, or tier 3 → 4 once its type has a branch (sim/branches). */
  canUpgrade(t: Tower): boolean { return canRaise(this, t); }

  collectValidTowerCells(): Cell[] { return this.road.collectValidTowerCells(); }

  // ------------------------------------------------------------------ commands

  execute(command: Command): CommandResult {
    if (this.isOver) return CommandResult.LevelOver;
    switch (command.kind) {
      case CommandKind.PickCard: return pickCard(this, command.a);
      case CommandKind.UnpickCard: return unpickCard(this);
      case CommandKind.PlaceSegment: return placeSegment(this, command.a);
      case CommandKind.PlaceTower: return placeTower(this, new Cell(command.a, command.b));
      case CommandKind.SkipOffer: return skipOffer(this);
      case CommandKind.Reroll: return reroll(this);
      case CommandKind.StartWaves: return this.startWaves();
      case CommandKind.CallNextStage: return this.callNextStage();
      case CommandKind.UpgradeTower: return upgradeTower(this, command.a);
      case CommandKind.SellTower: return sellTower(this, command.a);
      case CommandKind.SetPriority: return setPriority(this, command.a, command.b);
      case CommandKind.SetRally: return this.troops.setRally(new Cell(command.a, command.b)) ? CommandResult.Ok : CommandResult.InvalidPlacement;
      case CommandKind.ChooseBranch: return chooseBranch(this, command.a, (t) => promoteSquad(this, t), () => this.afterSpecial());
      case CommandKind.LearnSkill: return learnSkill(this, () => this.afterSpecial());
      case CommandKind.WyrmRetry: return wyrmRetry(this);
      case CommandKind.WyrmRelease: return wyrmRelease(this);
      case CommandKind.EngagePet: return engagePet(this);
      case CommandKind.MoveTower: return moveTower(this, command.a, new Cell(command.b, command.c));
      default: return CommandResult.InvalidIndex;
    }
  }

  /** A special card was spent: auras follow the new tier 4 / skill, and the offer closes. */
  private afterSpecial(): void {
    recomputeAuras(this);
    closeOffer(this, false);
  }

  private startWaves(): CommandResult {
    if (this.phase !== LevelPhase.Ready) return CommandResult.WrongPhase;
    this.events.add(SimEventKind.WavesStarted);
    this.startStage(1);
    return CommandResult.Ok;
  }

  /**
   * Calling the next stage early (DESIGN §4.8.7, M30 #17, Kingdom Rush style): between stages it cuts the countdown short;
   * during a running wave the next stage joins it on the road (overlapNextStage). Either way it pays
   * `earlyCallPointsPerSecond` per second of waiting skipped (earlyCallBonus), counted toward the next card like kills.
   */
  private callNextStage(): CommandResult {
    if (!this.canCallNext()) return CommandResult.WrongPhase;
    const reward = this.earlyCallBonus();
    if (this.phase === LevelPhase.BetweenWaves) this.startStage(this.stage + 1);
    else this.overlapNextStage();
    if (reward > 0) {
      this.events.add(SimEventKind.StageCalledEarly, this.stage, reward);
      this.addPoints(reward);
    }
    return CommandResult.Ok;
  }

  /**
   * Whether CALL NEXT works now (M30 #17): between stages with no card offer open, as ever; and during a running wave
   * (no offer open) once the newest stage on the road has begun to spawn, while the level has a stage left. A pure read.
   */
  canCallNext(): boolean {
    if (this.isOver || this.offerOpen || this.wyrmChoiceOpen || this.debugSandbox) return false;
    if (this.phase === LevelPhase.BetweenWaves) return true;
    return this.phase === LevelPhase.WaveRunning && this.stage < this.stages && this.spawnCursor > this.newestStageFirst;
  }

  /**
   * Points a call now pays (0 when it cannot be called): `earlyCallPointsPerSecond` per second skipped, floored. Between
   * stages that is the countdown left; during a wave, the spawns still to come plus a whole countdown — the wait before
   * the next stage would have started had the player let the wave finish. A pure read, for the CALL button.
   */
  earlyCallBonus(): number {
    if (!this.canCallNext()) return 0;
    let skipped = this.countdown;
    if (this.phase === LevelPhase.WaveRunning) {
      const last = this.schedule.length > 0 ? this.schedule[this.schedule.length - 1].tick : 0;
      skipped = Math.max(0, last - (this.tick - this.stageStartTick)) + this.balance.economy.betweenStageTicks;
    }
    if (skipped <= 0) return 0;
    return this.balance.economy.earlyCallPointsPerSecond.mulInt(skipped).divInt(SimConstants.TicksPerSecond).floorToInt();
  }

  /** `earlyCallBonus()` as a property (the pre-M30 name the HUD and the strategies read). */
  get earlyCallReward(): number { return this.earlyCallBonus(); }

  /**
   * Waves overlap (DESIGN §3.2, M30 #19): ticks after a stage's last spawn when the next stage starts on its own while
   * raiders are still on the road; 0 = it waits for the clear. A plain level's own (`LevelData.overlapTicks`); Iron and
   * Endless keep their own rhythm.
   */
  get overlapTicks(): number { return this.mods.endless !== null || this.mods.iron !== null ? 0 : this.level.overlapTicks; }

  /** Points skipping the open offer would pay: in-run offers only (the opening draft pays nothing). A pure read, for SKIP. */
  get skipReward(): number { return skipReward(this); }

  // ------------------------------------------------------------------ draft flow

  // ------------------------------------------------------------------ tick

  step(): void {
    this.events.clear();
    this.tick++;
    if (this.offerOpen || this.wyrmChoiceOpen) {
      // The action pauses while a card offer (or the Wyrm's choice, M40) is open, but the client keeps stepping (commands are logged against
      // `tick`). The spawn schedule reads the stage clock `tick − stageStartTick`, so the pause moves the stage's
      // origin with it; otherwise every spawn due during the pause would come out of the portal on one tick. Every
      // other timer (countdown, cooldowns, burns, disables, boosts) is a countdown the paused systems don't touch.
      this.stageStartTick++;
      return;
    }
    if (!this.isOver) this.activeTicks++;
    switch (this.phase) {
      case LevelPhase.BetweenWaves:
        if (this.debugSandbox) return;
        tickRareOffers(this);                             // M36: a rare's rally offers run on through the break
        tickWildPet(this);                                // Pets v2: the wild pet's clock runs on through the break
        if (this.offerOpen) return;
        this.countdown--;
        if (this.countdown <= 0) this.startStage(this.stage + 1);
        return;
      case LevelPhase.WaveRunning:
        if (!this.debugSandbox) { this.overlapIfDue(); spawnDue(this); }
        moveEnemies(this);
        if (this.phase !== LevelPhase.WaveRunning) return;
        tickEnemyAbilities(this);
        tickHexes(this);
        this.petSystem?.tick(this);                        // Pets v2: Rally Bark, Howl, Bite at the flag (sim/petSystem.ts)
        if (!this.debugSandbox) this.troops.tick(this.enemySlots, (e, dmg) => applyDamage(this, e, null, dmg, DamageType.Physical));
        tickSquads(this);
        tickBurns(this);
        tickShields(this);
        tickShells(this);
        if (this.ascension.active) tickBranches(this);        // tier 4: statuses, zones, skill clocks, auras, phoenixes, spiders
        fireTowers(this);
        tickHealers(this);
        if (this.wyrmChoiceOpen) return;                  // M40: the Wyrm flew off this tick; nothing ends until the choice
        tickWildPet(this);                                // Pets v2: appears, or runs off (sim/wildPet.ts)
        if (!this.debugSandbox) this.checkStageEnd();
        if (this.phase === LevelPhase.WaveRunning || this.phase === LevelPhase.BetweenWaves) { checkOfferThreshold(this); tickRareOffers(this); }
        return;
      default:
        return;
    }
  }

  /** @internal used by the system modules in ./systems */
  startStage(stage: number): void {
    this.stage = stage;
    const delay = stage === 1 ? this.balance.economy.firstStageDelayTicks : 0;
    this.schedule = wyrmStageSchedule(this, stage, delay) ?? rareStageSchedule(this, stage, delay) ?? stageSchedule(this.balance, this.level, this.levelNumber, stage, this.stages, delay, this.mods, this.seed);
    this.spawnCursor = 0;
    this.stageStartTick = this.tick;
    this.newestStageFirst = 0;
    this.phase = LevelPhase.WaveRunning;
    this.events.add(SimEventKind.StageStarted, stage);
    rallyHornAtStage(this, stage);
    wildPetAtStage(this, stage);
  }

  /**
   * The next stage joins the running wave (a mid-wave call, M30 #17, or the level's overlap, #19): its spawns merge with
   * what is left of the current schedule, both on a clock starting now (a spawn already due keeps its turn first).
   */
  private overlapNextStage(): void {
    const elapsed = this.tick - this.stageStartTick;
    const next = wyrmStageSchedule(this, this.stage + 1, 0) ?? rareStageSchedule(this, this.stage + 1, 0) ?? stageSchedule(this.balance, this.level, this.levelNumber, this.stage + 1, this.stages, 0, this.mods, this.seed);
    const merged: SpawnEntry[] = [];
    let i = this.spawnCursor, j = 0, first = 0;
    while (i < this.schedule.length || j < next.length) {
      const rest = i < this.schedule.length ? this.schedule[i] : null;
      const restTick = rest === null ? 0 : Math.max(0, rest.tick - elapsed);
      if (rest !== null && (j >= next.length || restTick <= next[j].tick)) { merged.push({ ...rest, tick: restTick }); i++; }
      else { if (j === 0) first = merged.length; merged.push(next[j++]); }
    }
    this.stage++;
    this.schedule = merged;
    this.spawnCursor = 0;
    this.stageStartTick = this.tick;
    this.newestStageFirst = first;
    this.events.add(SimEventKind.StageStarted, this.stage);
    rallyHornAtStage(this, this.stage);
    wildPetAtStage(this, this.stage);
  }

  /** The level's overlap (M30 #19): the next stage starts `overlapTicks` after the last spawn if raiders are still about. */
  private overlapIfDue(): void {
    const lag = this.overlapTicks;
    if (lag <= 0 || this.stage >= this.stages || this.spawnCursor < this.schedule.length || this._aliveEnemies === 0 || this.offerOpen) return;
    const last = this.schedule.length > 0 ? this.schedule[this.schedule.length - 1].tick : 0;
    if (this.tick - this.stageStartTick >= last + lag) this.overlapNextStage();
  }

  /** Kind of the road tile the enemy is currently on (by road distance; the base cell counts as Normal). */
  tileUnder(e: Enemy): TileKind {
    const kinds = this.road.roadTileKinds;
    const i = e.distance.floorToInt();
    return i >= 0 && i < kinds.length ? kinds[i] : TileKind.Normal;
  }

  /** Flyers over a Bridge (DESIGN §4.7) and Burrowers underground (ENEMIES_V2) cannot be targeted: systems/targeting.ts. */
  targetable(e: Enemy): boolean { return isTargetable(this, e); }

  /** @internal used by the system modules in ./systems */
  checkStageEnd(): void {
    if (this.spawnCursor < this.schedule.length || this._aliveEnemies > 0) return;
    // Overlapping stages (a mid-wave call or the level's overlap) clear together: each is cleared, in order.
    const from = this.stagesCleared + 1;
    this.stagesCleared = this.stage;
    landAllShells(this);                                  // nothing is left to hit: shells still in the air land now
    if (this.zones.length > 0) clearZones(this);          // nor anything to stand in a zone
    for (let s = from; s <= this.stage; s++) {
      this.events.add(SimEventKind.StageCleared, s);
      if (s >= this.stages) { this.endLevel(LevelPhase.Won); return; }
      if (s === this.stage) { this.phase = LevelPhase.BetweenWaves; this.countdown = this.balance.economy.betweenStageTicks; }
      if (s % this.balance.cards.rerollEveryWaves === 0 && this.rerollCharges < this.balance.cards.rerollBank) {
        this.rerollCharges++;
        this.events.add(SimEventKind.RerollCharged, this.rerollCharges);
      }
    }
  }

  /** @internal used by the system modules in ./systems */
  endLevel(phase: LevelPhase): void {
    this.phase = phase;
    const won = phase === LevelPhase.Won;
    // An Endless run's chest is rolled at the player's progress (its `through`), not at the level whose ground it borrows.
    const chestLevel = this.mods.endless ? this.mods.endless.through : this.levelNumber;
    // Daily Road (MP-20) banks no battle chest: its rewards are the day's, paid once (meta/dailyRoad.ts).
    // MP-21/MP-28: an event-mode or challenge-code run neither (Boss Week, Race Week and the Odyssey pay their own rewards).
    // MP-25: a late first clear rolls a rarity higher; a won farm twist pays its gold bonus
    const extras = { rarityBump: battleChestBump(this.balance, chestLevel, won, this.firstClear), goldBonus: won && this.mods.farm ? this.mods.farm.bonus : Fix64.Zero };
    if (!this.mods.daily && !this.mods.event && !this.mods.challenge) this.chest = rollChest(this.balance, chestLevel, battleChestBand(this.balance, chestLevel, won, this.leaks, this.firstClear), this.rng.nextSeed(), true, extras);
    this.events.add(won ? SimEventKind.LevelWon : SimEventKind.LevelLost);
    if (this.chest) this.events.add(SimEventKind.ChestRolled, this.chest.rarity, this.chest.band);
  }

  /** @internal used by the system modules in ./systems */
  addPoints(delta: number): void {
    this.points += delta;
    this.events.add(SimEventKind.PointsChanged, this.points, delta);
  }

  /** @internal used by the system modules in ./systems */
  allocateEnemy(): Enemy {
    for (const e of this.enemySlots) if (!e.alive) return e;
    throw new Error("Enemy pool exhausted");
  }

  /**
   * Dev only: place a tower with no offer and no cost, for the captures that show one tower at a time.
   *
   * Everything `placeTower` does apart from the draft flow around it. Nothing in the game calls this — it exists so
   * a capture can stage a scene the game would take twenty minutes of play to reach.
   */
  debugAddTower(archetype: number, cell: Cell, tier = 1): Tower | null {
    if (!this.road.tryPlaceTower(cell)) return null;
    this.troops.reroute();
    rerouteSquads(this);
    const t = this.allocateTower();
    t.id = this.nextTowerId++;
    t.archetype = archetype;
    t.alive = true;
    t.cell = cell;
    t.tier = tier;
    t.cooldown = 0;
    t.disabledTicksLeft = 0;
    t.priority = TargetPriority.First;
    t.kills = 0;
    t.auraDamageMult = Fix64.One;
    t.auraPointsMult = Fix64.One;
    t.beamTarget = 0;
    t.beamTicks = 0;
    resetBranchState(t);
    recomputeAuras(this);                 // as a placed tower does (draftFlow): a staged beacon buffs its neighbours
    raiseSquad(this, t);
    this.events.add(SimEventKind.TowerPlaced, t.id, cell.x, cell.y);
    return t;
  }

  /** Dev only: stage type `archetype` at tier 4 as branch `slot` with these skills (branches/ascension.ts debugSetBranch). */
  debugSetBranch(archetype: number, slot: number, skillA = false, skillB = false): void { debugSetBranch(this, archetype, slot, skillA, skillB); recomputeAuras(this); }

  /** Dev only: remove every placed tower (the fortress stays), so the next staged scene starts clean. */
  debugClearTowers(): void {
    for (const t of this.towerSlots) {
      if (!t.alive || this.balance.towers[t.archetype].isBase) continue;
      this.road.removeTower(t.cell);
      disbandSquad(this, t.id);
      t.alive = false;
    }
    recomputeAuras(this);
  }

  /** Dev only: showcase mode — no scheduled spawns, no defeat, the clock always running. */
  debugSandboxOn(): void {
    this.debugSandbox = true;
    this.debugNoDefeat = true;
    this.offerOpen = false;
    for (const e of this.enemySlots) if (e.alive) { e.alive = false; this._aliveEnemies--; }
    this.phase = LevelPhase.WaveRunning;
  }

  /** Dev only (M36 captures): the next stage to start is a rare stage, whatever the roll (`window.__td.level.sim.debugForceRareNextStage()`). */
  debugForceRareNextStage(): void { this.forceRareNextStage = true; }
  /** Dev only (M40): the next stage to start is the Starfall Wyrm's (Iron: the first eligible block), whatever the roll. */
  debugForceWyrmNextStage(): void { this.forceWyrmNextStage = true; }

  /** Dev only: remove every live enemy. */
  debugClearEnemies(): void {
    for (const e of this.enemySlots) if (e.alive) { e.alive = false; this._aliveEnemies--; }
  }

  /**
   * Dev only: one enemy at the portal, now, outside the wave schedule (`isBoss` for the boss-moment captures). `hpMult`
   * scales the archetype's HP and may be fractional (taken to the nearest 1/DEBUG_HP_MULT_STEPS; not finite or not
   * positive means 1); a boss also gets the boss HP multiplier and boss points, as the wave schedule gives one.
   */
  debugSpawn(archetype: number, hpMult: number | Fix64 = 1, isBoss = false, affix = -1): void {
    const mult = typeof hpMult !== "number" ? hpMult
      : Number.isFinite(hpMult) && hpMult > 0 ? Fix64.fromFraction(Math.round(hpMult * DEBUG_HP_MULT_STEPS), DEBUG_HP_MULT_STEPS) : Fix64.One;
    const economy = this.balance.economy;
    spawn(this, { tick: this.tick, archetype, hpMult: isBoss ? mult.mul(bossHpMultOf(this.balance, this.level)) : mult, isBoss, pointsOverride: isBoss ? economy.bossPoints : -1, affix: isBoss ? -1 : affix });
  }

  /** @internal used by the system modules in ./systems */
  allocateTower(): Tower {
    for (const t of this.towerSlots) if (!t.alive) return t;
    throw new Error("Tower pool exhausted");
  }

  // ------------------------------------------------------------------ hash

  /** Canonical FNV-1a hash of the full simulation state (see systems/simHash). */
  computeHash(): bigint { return computeHash(this); }

}
